import { When } from 'react-if';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { XCircleIcon } from '@heroicons/react/20/solid';

import { env } from '../../../../../common/lib/env';
import { Button } from '../../../../../common/Atoms/Button';
import {
  Rgb,
  detectSolidBackground,
  loadPixels,
  pixelsToPng,
  removeBackground,
  trimTransparent,
} from '../../../../../common/lib/logoBackground';
import { useApiRequest } from '../../../../hooks/useApiRequest';

interface IFileUploadProps {
  url?: string;
  setUrl: (url: string) => void;
  /** Unique input id, needed when more than one upload is rendered on a page */
  id?: string;
  invalid?: {
    message: string;
  };
}

export function FileUpload({
  url,
  setUrl,
  invalid,
  id = `dropzone-file`,
}: IFileUploadProps) {
  const [file, setFile] = useState(null);
  // const [percent, setPercent] = useState(0)
  const [uploading, setUploading] = useState(false);
  /** Set when the uploaded logo has a solid background we can offer to strip */
  const [solidBackground, setSolidBackground] = useState<{ pixels: ImageData; color: Rgb } | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  /** The file currently shown. Async work for any other file is stale and must not touch state. */
  const currentFile = useRef<File | null>(null);

  const baseRequest = useApiRequest();
  function changeLogo() {
    currentFile.current = null;
    setFile(null);
    setUrl(null);
    setUploading(false);
    setSolidBackground(null);
    setOriginalUrl(null);
  }

  async function uploadLogo(blob: Blob, filename: string) {
    const formData = new FormData();
    formData.append(`image`, blob, filename);

    const { data } = await baseRequest.post(`${env.baseApiUrl}email-templates/logo`,
      formData,
      {
        headers: {
          'Content-Type': `multipart/form-data`,
        },
      },
    );

    return data.url as string;
  }

  // Handles a file being picked

  async function handleChange(event) {
    try {
      const fileEvent: File = event.target.files[0];
      currentFile.current = fileEvent;
      setFile(fileEvent);
      setSolidBackground(null);
      setOriginalUrl(null);

      setUploading(true);

      const uploadedUrl = await uploadLogo(fileEvent, fileEvent.name);

      setUploading(false);
      toast.success(`Logo uploaded successfully`);
      setUrl(uploadedUrl);

      // Detection runs after upload so a decode failure never blocks the logo
      try {
        const pixels = await loadPixels(fileEvent);
        const color = detectSolidBackground(pixels);
        if (color && currentFile.current === fileEvent) {
          setSolidBackground({ pixels, color });
        }
      }
      catch {
        // Not an image we can decode, no offer
      }
    }
    catch (err) {
      toast.error(err.response?.data || err.message);
      setFile(null);
      setUploading(false);
    }
  }

  async function handleRemoveBackground() {
    if (!solidBackground || removing) {
      return;
    }
    const sourceFile = currentFile.current;

    try {
      setRemoving(true);

      const pixels = new ImageData(new Uint8ClampedArray(solidBackground.pixels.data), solidBackground.pixels.width, solidBackground.pixels.height);
      removeBackground(pixels, solidBackground.color);

      const png = await pixelsToPng(trimTransparent(pixels));
      const filename = (file?.name ?? `logo`).replace(/\.\w+$/, ``) + `.png`;
      const newUrl = await uploadLogo(png, filename);

      if (currentFile.current !== sourceFile) {
        return;
      }

      setOriginalUrl(url);
      setUrl(newUrl);
      setSolidBackground(null);
      toast.success(`Background removed`);
    }
    catch (err) {
      toast.error(err.response?.data || err.message);
    }
    finally {
      setRemoving(false);
    }
  }

  function undoRemoveBackground() {
    setUrl(originalUrl);
    setOriginalUrl(null);
  }

  return (
    <div className={ `flex items-center w-3/4 mt-2` }>
      <When condition={ !file && !url }>
        <label htmlFor={ id }
          className={ `flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100` }>
          <div className={ `flex flex-col items-center justify-center pt-2 pb-2` }>
            <svg aria-hidden={ `true` }
              className={ `w-8 h-8 mb-3 text-gray-400` }
              fill={ `none` }
              stroke={ `currentColor` }
              viewBox={ `0 0 24 24` }
              xmlns={ `http://www.w3.org/2000/svg` }>
              <path strokeLinecap={ `round` }
                strokeLinejoin={ `round` }
                strokeWidth={ `2` }
                d={ `M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12` }>
              </path>
            </svg>
            <p className={ `mb-2 text-sm text-gray-500 dark:text-gray-400` }>
              <span className={ `font-semibold` }>{ `Click to upload` }</span>
            </p>
            <p className={ `text-xs text-gray-500 dark:text-gray-400` }>{ `PNG, JPG or JPEG (Recommended Max Size 550 x 150px) (Resized automatically)` }</p>
          </div>
          <input
            id={ id }
            type={ `file` }
            className={ `hidden` }
            disabled={ uploading === true }
            onChange={ handleChange }
            accept={ `image/png, image/jpeg, image/jpg` }
          />
        </label>
      </When>
      <When condition={ !!invalid }>
        <p className={ `text-red-500` }>{ invalid?.message } </p>
      </When>
      <When condition={ uploading === true }>
        <div >
          <p>{ `Uploading...` }</p>
          <div className={ `h-1 w-full bg-neutral-200 dark:bg-neutral-600` }>
          </div>
        </div>
      </When>
      <When condition={ uploading !== true && url }>
        <div className={ `w-full` }>
          <div className={ `rounded-lg p-6 uploadBackground` }>
            <a className={ `float-right` }
              href={ `#` }
              title={ `remove logo` }
              onClick={ changeLogo }> <XCircleIcon className={ `h-8 w-8 text-red-400 hover:text-red-600` }/></a>
            <img
              src={ url }
              className={ `App-logo` }
              alt={ `logo` } />
          </div>
          <When condition={ !!solidBackground }>
            <div className={ `mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300` }>
              <span>{ `Looks like this logo has a solid background. Remove it so it sits cleanly on coloured headers?` }</span>
              <Button size={ `sm` }
                loading={ removing }
                disabled={ removing }
                onClick={ handleRemoveBackground }>{ `Remove background` }</Button>
              <Button size={ `sm` }
                color={ `white` }
                disabled={ removing }
                onClick={ () => setSolidBackground(null) }>{ `Keep as is` }</Button>
            </div>
          </When>
          <When condition={ !!originalUrl }>
            <p className={ `mt-2 text-sm text-gray-600 dark:text-gray-300` }>
              { `Background removed. ` }
              <a href={ `#` }
                className={ `underline cursor-pointer` }
                onClick={ e => { e.preventDefault(); undoRemoveBackground(); } }>{ `Undo` }</a>
            </p>
          </When>
        </div>
      </When>
    </div>

  );
}
